import { graphql } from "gatsby"
import Img, { FluidObject, GatsbyImageOptionalProps } from "gatsby-image"
import React from "react"
import Breakpoints from "../../components/shared/breakpoints"
import withVariants from "../util/withVariants"

export interface IBreakpointImageVariants {
  sm?: FluidObject
  md?: FluidObject
  lg?: FluidObject
}

interface IBreakpointImageFluidProps extends GatsbyImageOptionalProps {
  imageVariants: IBreakpointImageVariants
  fluid: never
}
interface IBreakpointImageVariantProps extends GatsbyImageOptionalProps {
  fluid: FluidObject | FluidObject[]
  imageVariants: never
}

const BreakpointImage = ({
  imageVariants,
  fluid,
  imgStyle = { objectFit: "contain" },
  ...rest
}: IBreakpointImageFluidProps | IBreakpointImageVariantProps) => {
  const sources = [imageVariants?.sm]

  if (imageVariants?.lg) {
    sources.push({
      ...imageVariants.lg,
      media: `(min-width: ${Breakpoints.lg})`,
    })
  }

  if (imageVariants?.md) {
    sources.push({
      ...imageVariants.md,
      media: `(min-width: ${Breakpoints.md})`,
    })
  }

  return (
    <Img
      // @ts-ignore - this type is correct
      fluid={imageVariants ? sources : fluid}
      imgStyle={imgStyle}
      {...rest}
    />
  )
}

export type TContentfulImage = {
  fluid: FluidObject
  description?: string
  file?: {
    details?: { image?: { height: number; width: number } }
    url: string
  }
}

export interface IContentfulResponsiveImage extends GatsbyImageOptionalProps {
  __typename: "ContentfulResponsiveImage"
  mobile: TContentfulImage
  tablet?: TContentfulImage
  desktop?: TContentfulImage
}

/**
 * Adapter function for Contentful Responsive Image to Breakpoint Image
 */
export const ContentfulResponsiveImage = ({
  mobile,
  tablet,
  desktop,
  ...rest
}: IContentfulResponsiveImage) => {
  return (
    // Wrap images in their max size so dimensions can be controlled on Contentful
    <div
      style={{
        // Fallback from desktop to tablet to mobile images
        // Ideally if we only have a mobile image it should be large enough to scale onto desktop
        maxWidth: (desktop || tablet || mobile)?.file?.details?.image?.width,
        margin: "0 auto",
      }}
    >
      {mobile?.fluid ? (
        // @ts-ignore - this type is correct
        <BreakpointImage
          imageVariants={{
            sm: mobile.fluid,
            md: tablet?.fluid,
            lg: desktop?.fluid,
          }}
          alt={mobile?.description || ""}
          {...rest}
        />
      ) : (
        mobile?.file?.url && (
          <picture>
            {desktop?.file?.url ? (
              <source
                media={`(min-width: ${Breakpoints.lg})`}
                srcSet={`https:${desktop?.file?.url}`}
              />
            ) : null}
            {tablet?.file?.url ? (
              <source
                media={`(min-width: ${Breakpoints.md})`}
                srcSet={`https:${tablet?.file?.url}`}
              />
            ) : null}
            <img
              src={mobile.file.url}
              alt={mobile.description}
              style={{ maxWidth: "100%" }}
            />
          </picture>
        )
      )}
    </div>
  )
}

export const ResponsiveImageFragment = graphql`
  fragment ContentfulResponsiveImage on ContentfulResponsiveImage {
    contentful_id
    mobile {
      fluid(quality: 90, maxWidth: 960) {
        ...GatsbyContentfulFluid_withWebp
      }
      file {
        details {
          image {
            height
            width
          }
        }
        url
      }
      description
    }
    tablet {
      fluid(quality: 90, maxWidth: 1440) {
        ...GatsbyContentfulFluid_withWebp
      }
      file {
        details {
          image {
            height
            width
          }
        }
        url
      }
    }
    desktop {
      fluid(quality: 90, maxWidth: 1920) {
        ...GatsbyContentfulFluid_withWebp
      }
      file {
        details {
          image {
            height
            width
          }
        }
        url
      }
    }
  }
`
export const RESPONSIVE_IMAGE_TYPENAME = "ContentfulResponsiveImage"
export default withVariants(BreakpointImage)
